import { Button, ResponsiveTable, Spinner, useTranslation } from '@hbs-us/ui';
import { getRouteApi, Link } from '@tanstack/react-router';
import { downloadCredentialed } from '../../lib/download.js';
import { useMoneyFormatters } from '../_money/useMoneyFormatters.js';
import { payrollCsvUrl, payrollPdfUrl } from './exportLinks.js';
import { usePayrollRunQuery } from './usePayrollQuery.js';

/**
 * The `/payroll/runs/$id` route — a single LOCKED, immutable payroll run. Renders the snapshotted
 * per-employee entries (read from payroll_entries server-side, NEVER recomputed from live hours) +
 * the PDF / CSV export links (api-host GET, reused engines via `exportLinks`).
 *
 * The gross-pay figures are the SERVER snapshot — this view renders them verbatim, performing NO
 * OT / lunch / ÷52 math (D-03 / T-22-payroll). Money numerals are font-mono with AA contrast.
 */

const routeApi = getRouteApi('/payroll/runs/$id');

export function PayrollRunDetailRoute() {
  const { t } = useTranslation();
  const { id } = routeApi.useParams();
  const runId = Number(id);
  const { formatCurrency } = useMoneyFormatters();
  const query = usePayrollRunQuery(runId);

  if (query.isPending) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <Spinner label={t('common:status.loading')} />
      </div>
    );
  }

  if (query.isError || !query.data.ok || !query.data.run) {
    return (
      <section
        role="alert"
        aria-live="assertive"
        className="flex h-full flex-col items-center justify-center gap-4 bg-background p-16 text-center"
      >
        <h1 className="font-display font-semibold text-2xl text-foreground">
          {t('payroll:detail.errorHeading')}
        </h1>
        <p className="max-w-sm text-muted-foreground text-sm">{t('payroll:detail.errorBody')}</p>
        <Button asChild variant="secondary">
          <Link to="/payroll/history">{t('payroll:detail.backToHistory')}</Link>
        </Button>
      </section>
    );
  }

  const run = query.data.run;
  const entries = query.data.entries ?? [];

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-semibold text-3xl text-foreground">
            {t('payroll:detail.title')}
          </h1>
          <p className="mt-1 font-mono text-muted-foreground text-sm">
            {run.weekStart} – {run.weekEnd}
          </p>
          <p className="mt-1 text-muted-foreground text-sm">
            {t('payroll:detail.lockedLine', {
              lockedAt: run.lockedAt,
              lockedBy: run.lockedByName ?? '—',
            })}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            onClick={() =>
              void downloadCredentialed(payrollPdfUrl(run.id), `payroll-run-${run.id}.pdf`).catch(
                (err) => console.error('payroll pdf download failed', err),
              )
            }
          >
            {t('payroll:detail.exportPdf')}
          </Button>
          <Button
            variant="secondary"
            onClick={() =>
              void downloadCredentialed(payrollCsvUrl(run.id), `payroll-run-${run.id}.csv`).catch(
                (err) => console.error('payroll csv download failed', err),
              )
            }
          >
            {t('payroll:detail.exportCsv')}
          </Button>
        </div>
      </header>

      <section
        aria-label={t('payroll:summary.regionLabel')}
        className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2"
      >
        <SummaryCard
          label={t('payroll:detail.totalGross')}
          value={formatCurrency(run.totalGrossPay)}
          mono
        />
        <SummaryCard label={t('payroll:detail.employees')} value={String(run.employeeCount)} />
      </section>

      <ResponsiveTable className="rounded-md border border-border bg-card">
        <table className="w-full border-collapse text-sm">
          <caption className="sr-only">{t('payroll:detail.caption')}</caption>
          <thead>
            <tr className="border-border border-b text-left text-muted-foreground">
              <th scope="col" className="px-4 py-3 font-medium">
                {t('payroll:grid.employee')}
              </th>
              <th scope="col" className="px-4 py-3 font-medium">
                {t('payroll:grid.payType')}
              </th>
              <th scope="col" className="px-4 py-3 text-right font-medium">
                {t('payroll:grid.regularHours')}
              </th>
              <th scope="col" className="px-4 py-3 text-right font-medium">
                {t('payroll:grid.overtimeHours')}
              </th>
              <th scope="col" className="px-4 py-3 text-right font-medium">
                {t('payroll:grid.grossPay')}
              </th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.employeeId} className="border-border/60 border-b last:border-b-0">
                <td className="px-4 py-3 font-medium text-foreground">{e.employeeName}</td>
                <td className="px-4 py-3 text-muted-foreground">{e.payType}</td>
                <td className="px-4 py-3 text-right font-mono text-foreground">
                  {e.payType === 'Hourly' ? e.regularHours.toFixed(2) : '—'}
                </td>
                <td className="px-4 py-3 text-right font-mono text-foreground">
                  {e.payType === 'Hourly' ? e.overtimeHours.toFixed(2) : '—'}
                </td>
                {/* The snapshotted (immutable) gross-pay, rendered verbatim. */}
                <td className="px-4 py-3 text-right font-mono font-semibold text-foreground">
                  {formatCurrency(e.grossPay)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-border border-t">
              <td className="px-4 py-3 font-semibold text-foreground" colSpan={4}>
                {t('payroll:grid.total')}
              </td>
              <td className="px-4 py-3 text-right font-mono font-semibold text-foreground">
                {formatCurrency(run.totalGrossPay)}
              </td>
            </tr>
          </tfoot>
        </table>
      </ResponsiveTable>
    </div>
  );
}

function SummaryCard({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-md border border-border bg-card p-6">
      <p className="text-muted-foreground text-sm">{label}</p>
      <p
        className={
          mono
            ? 'mt-1 font-display font-mono font-semibold text-2xl text-foreground'
            : 'mt-1 font-display font-semibold text-2xl text-foreground'
        }
      >
        {value}
      </p>
    </div>
  );
}
